import React, { useMemo } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Grid, Typography } from "@mui/material"
import { ApiQueryTable } from "../../../../../../../core/api/components/ApiQueryTable"
import { MRT_ColumnDef } from "material-react-table"
import { CreateOrEditPriceListFormValues, Products } from "../../../priceListTypes"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { useFormikContext } from "formik"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useColumnFactory } from "../../../../../../../core/api/useTableColumns"

const useStyles = makeStyles()((_theme) => ({
    container: {
        gap: "2rem",
        flexDirection: "column",
    },
    tableContainer: {
        maxHeight: "100%",
        borderRadius: ".75rem",
        padding: "1em 1em 3em 1em",
    },
    tablePaper: {
        boxShadow: "none",
        borderRadius: ".75rem",
    },
    tableHeadCell: {
        border: "none",
        color: _theme.palette.tailwind.slate[500],
    },
    tableHeadRow: {
        boxShadow: "none",
        background: _theme.palette.tailwind.slate[50],
    },
    topToolbar: {
        borderRadius: ".75rem",
    },
    tableTitle: {
        fontSize: "2rem",
        fontWeight: 500,
        color: _theme.palette.tailwind.slate[600],
    },
}))
export function FormProductsContent() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { values } = useFormikContext<CreateOrEditPriceListFormValues>()

    const columnFactory = useColumnFactory<Products>()

    const columns = useMemo<MRT_ColumnDef<Products>[]>(
        () => [
            columnFactory.statusColumn({
                header: l10n.getString("create-or-edit-price-list-products-table-header-product-no"),
                accessorKey: "productNumber",
            }),
            columnFactory.statusColumn({
                header: l10n.getString("create-or-edit-price-list-products-table-header-product-no"),
                accessorKey: "productNumber",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("create-or-edit-price-list-products-table-header-name"),
                accessorKey: "name",
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("create-or-edit-price-list-products-table-header-normal-price"),
                    id: "price",
                },
                (row) => row.price,
            ),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("create-or-edit-price-list-products-table-header-adjusted-price"),
                    id: "adjustedPrice",
                },
                (row) => row.adjustedPrice,
            ),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("create-or-edit-price-list-products-table-header-status"),
                    id: "status",
                },
                (row) => row?.isActive,
            ),
        ],
        [l10n],
    )

    return (
        <Grid container className={classes.container}>
            <Grid item xs={12}>
                <ApiQueryTable
                    isLoading={false}
                    isFetching={false}
                    isError={false}
                    data={values.products}
                    columns={columns}
                    enableClickToCopy={false}
                    enableColumnOrdering
                    enableStickyHeader
                    enableDensityToggle={false}
                    initialState={{ density: "spacious" }}
                    enableHiding={false}
                    enableBottomToolbar={false}
                    muiTablePaperProps={{ className: classes.tablePaper }}
                    muiTableContainerProps={{ className: classes.tableContainer }}
                    muiTableHeadCellProps={{ className: classes.tableHeadCell }}
                    muiTableHeadRowProps={{ className: classes.tableHeadRow }}
                    muiTopToolbarProps={{ className: classes.topToolbar }}
                    enableColumnDragging={false}
                    renderTopToolbar={() => (
                        <Box display="flex" alignItems="center" justifyContent="space-between" padding="1rem">
                            <LocalizedStrict id="create-or-edit-price-list-products-table-title">
                                <Typography className={classes.tableTitle} variant="h6">
                                    Products
                                </Typography>
                            </LocalizedStrict>
                        </Box>
                    )}
                />
            </Grid>
        </Grid>
    )
}
